<script setup>
definePage({ meta: { layout: 'customer', title: '낭만센터' } });

import { getBoardName } from '@/composables/functions';
import NmcLnb from '@/components/NmcLnb.vue';
import { NmcBoardList, NmcBoardView, NmcBoardWrite, EssayList } from '@/components/board';
import { useStore } from 'vuex';
import { canonicalUrl, buildHeadMeta } from '@/utils/seo';

const store = useStore();
const board = ref(null);
const boardId = ref(0);
const articleId = ref(0);
const route = useRoute();
const router = useRouter();

// board별 SEO 메타. 상세 페이지(articleId 있음)도 일단 목록 메타 사용.
// TODO: 상세 페이지는 API 응답의 게시글 제목/본문을 동적 주입 (별도 작업)
const BOARD_SEO_META = {
  notice: {
    title: '낭만만세력 공지, 업데이트, 사용법, 혜택',
    description:
      '낭만만세력 서비스 업데이트, 신규 기능, 무료 체험, 분석권 할인, 포인트 혜택, 사주조회 사용법까지 한눈에 확인할 수 있는 공식 공지사항입니다.',
  },
  word: {
    title: '오행, 십성, 60갑자, 신살, 궁합 해설',
    description:
      '화·목·토·금·수 오행부터 십성, 60갑자, 형충파해합, 신살, 야자시·조자시, 동지·입춘까지. 어려운 명리학을 쉽게 풀어내는 낭만만세력 콘텐츠.',
  },
  event: {
    title: '무료사주풀이, 무료궁합, 충전보너스 이벤트',
    description:
      '무료 사주풀이, 무료 궁합 분석, 2026 신년운세 이벤트, PC 충전 5% 추가 보너스까지 낭만만세력의 진행 중인 이벤트를 한눈에 확인하세요.',
  },
  essay: {
    title: '좋은글, 좋은시, 감성에세이, 오늘의 간지',
    description:
      '매주 수요일, 그날의 간지에 담긴 하루의 기운을 시와 명언으로 풀어내는 낭만에세이. 좋은 글 한 편과 함께 일상에 사주의 낭만을 더해보세요.',
  },
  faq: {
    title: '사주 조회, 무료 만세력, 이용법 FAQ',
    description:
      '사주 조회 방법, 무료 이용, 출생시간 모를 때 조회, 사주 간지 역추적, 결제·회원 안내까지. 낭만만세력 이용 중 궁금한 질문을 빠르게 해결하세요.',
  },
};

// useSeoMeta는 reactive 분기 함수형 입력을 공식 지원하지 않으므로
// board 파라미터에 따라 동적 전환이 필요한 이 페이지는 useHead를 사용.
useHead(() => {
  const meta = BOARD_SEO_META[route.params?.board] || { title: '낭만센터' };
  return buildHeadMeta({
    title: meta.title,
    description: meta.description,
    url: canonicalUrl(route.fullPath),
  });
});

const setData = () => {
  boardId.value = getBoardName(route.params.board ?? 'notice', 'boardId');
  board.value = route.params.board ?? 'notice';
  articleId.value = route.params.articleId ?? 0;
};

onMounted(() => {
  if (!route.params.board) {
    router.replace('/nangman-center/notice');
    return;
  }
  setData();
});

onUnmounted(() => {
  store.dispatch('saveCurrentBoard', '');
  store.dispatch('saveCurrentBoardPage', 1);
});

watch(
  () => route.params.board,
  (newBoard) => {
    const currentBoard = store.state.boards.currentBoard;
    if (currentBoard !== newBoard) {
      store.dispatch('saveCurrentBoardPage', 1);
      store.dispatch('saveCurrentEssayPage', 1);
      store.dispatch('saveCurrentEssayKeywords', '');
      store.dispatch('saveCurrentBoardKeywords', '');
      store.dispatch('saveCurrentEssaySort', { key: 'new', dir: 'desc' });
    }
    setData();
  },
);
watch(
  () => route.params.boardId,
  () => setData(),
);
watch(
  () => route.params.articleId,
  () => setData(),
);
</script>

<template>
  <section class="inner-base-2">
    <nav class="cust-gnb">
      <NmcLnb />
    </nav>
    <section class="cust-body">
      <div v-if="boardId">
        <NmcBoardList v-if="!articleId && board !== 'essay'" :board="board" :board-id="boardId" />
        <NmcBoardView
          v-if="articleId && articleId !== 'write' && board !== 'essay'"
          :board="board"
          :board-id="boardId"
          :article-id="articleId"
        />
        <NmcBoardWrite
          v-if="articleId === 'write' && board !== 'essay'"
          :board="board"
          :board-id="boardId"
          :article-id="articleId"
        />
        <EssayList v-if="!articleId && board === 'essay'" :board="board" :board-id="boardId" />
      </div>
    </section>
  </section>
</template>
<style lang="scss" scoped>
@import url('/src/assets/nangman_style/layout.scss');
</style>
